import type {
	SketchEntityReferenceRebuildBatchPlan,
	SketchEntityReferenceRebuildBatchPlannerInput,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceRebuildBatchPlanner';
import {
	createSketchEntityReferenceIntentBatchKey,
	createSketchEntityReferenceSingleBatchKey,
} from '../utils/createSketchEntityReferenceBatchKey';

/**
 * 转换实体重建原子批次规划器。
 *
 * 先保留用户选择意图表达的复合成员，再按真实来源 anchor 和目标点身份计算连通分量。
 * 该 Module 只处理 plain data，不读取或修改 repository、Object3D 与参数 Solver。
 */
export class SketchEntityReferenceRebuildBatchPlanner {
	/**
	 * 规划当前 checkpoint 的原子重建批次。
	 *
	 * @param input 冻结引用与端点身份依据。
	 * @returns 按目标草图和稳定 batchKey 排序的批次。
	 */
	public plan(
		input: SketchEntityReferenceRebuildBatchPlannerInput,
	): SketchEntityReferenceRebuildBatchPlan[] {
		const orderedReferences = [...input.references].sort((left, right) => (
			left.targetSketchId.localeCompare(right.targetSketchId)
			|| left.referenceId.localeCompare(right.referenceId)
		));
		const semanticKeyByReferenceId = new Map<string, string>();
		const referenceIdsBySemanticKey = new Map<string, string[]>();

		orderedReferences.forEach((reference) => {
			const semanticKey = this.resolveSemanticKey(reference);
			semanticKeyByReferenceId.set(reference.referenceId, semanticKey);
			const referenceIds = referenceIdsBySemanticKey.get(semanticKey) ?? [];
			referenceIds.push(reference.referenceId);
			referenceIdsBySemanticKey.set(semanticKey, referenceIds);
		});

		const parentBySemanticKey = new Map<string, string>();
		referenceIdsBySemanticKey.forEach((_referenceIds, semanticKey) => {
			parentBySemanticKey.set(semanticKey, semanticKey);
		});
		const semanticKeyByEndpointToken = new Map<string, string>();

		orderedReferences.forEach((reference) => {
			const semanticKey = semanticKeyByReferenceId.get(reference.referenceId);
			if (!semanticKey) {
				return;
			}
			const evidence = input.endpointEvidenceByReferenceId.get(reference.referenceId);
			const endpointTokens = [
				...(evidence?.sourceAnchorIds ?? []).map(anchorId => (
					`${reference.targetSketchId}::source-anchor::${anchorId}`
				)),
				...(evidence?.targetPointIds ?? []).map(pointId => (
					`${reference.targetSketchId}::target-point::${pointId}`
				)),
			];
			endpointTokens.forEach((endpointToken) => {
				const existingSemanticKey = semanticKeyByEndpointToken.get(endpointToken);
				if (existingSemanticKey) {
					this.union(parentBySemanticKey, existingSemanticKey, semanticKey);
					return;
				}
				semanticKeyByEndpointToken.set(endpointToken, semanticKey);
			});
		});

		const semanticKeysByRoot = new Map<string, string[]>();
		referenceIdsBySemanticKey.forEach((_referenceIds, semanticKey) => {
			const root = this.findRoot(parentBySemanticKey, semanticKey);
			const semanticKeys = semanticKeysByRoot.get(root) ?? [];
			semanticKeys.push(semanticKey);
			semanticKeysByRoot.set(root, semanticKeys);
		});

		return [...semanticKeysByRoot.values()]
			.map((semanticKeys) => {
				const orderedSemanticKeys = [...semanticKeys].sort();
				const referenceIds = orderedSemanticKeys
					.flatMap(semanticKey => referenceIdsBySemanticKey.get(semanticKey) ?? [])
					.sort();
				const firstReference = orderedReferences.find(reference => (
					referenceIds.includes(reference.referenceId)
				));
				if (!firstReference) {
					throw new Error('转换实体重建批次缺少目标草图身份。');
				}
				return {
					targetSketchId: firstReference.targetSketchId,
					batchKey: `${firstReference.targetSketchId}::batch::${orderedSemanticKeys.join('|')}`,
					rollbackKeys: orderedSemanticKeys,
					referenceIds,
				};
			})
			.sort((left, right) => (
				left.targetSketchId.localeCompare(right.targetSketchId)
				|| left.batchKey.localeCompare(right.batchKey)
			));
	}

	/**
	 * 解析引用在端点合并前的稳定语义 key。
	 *
	 * @param reference 当前冻结引用。
	 * @returns 复合意图 key 或独立引用 key。
	 */
	private resolveSemanticKey(
		reference: SketchEntityReferenceRebuildBatchPlannerInput['references'][number],
	): string {
		const selectionIntent = reference.selectionIntent;
		if (selectionIntent && selectionIntent.intentKind !== 'single-source') {
			return createSketchEntityReferenceIntentBatchKey(
				reference.targetSketchId,
				selectionIntent.intentId,
			);
		}
		return createSketchEntityReferenceSingleBatchKey(
			reference.targetSketchId,
			reference.referenceId,
		);
	}

	/**
	 * 查找并压缩语义 key 的并查集根节点。
	 *
	 * @param parentBySemanticKey 并查集父节点表。
	 * @param semanticKey 待查询语义 key。
	 * @returns 根节点语义 key。
	 */
	private findRoot(
		parentBySemanticKey: Map<string, string>,
		semanticKey: string,
	): string {
		const parent = parentBySemanticKey.get(semanticKey);
		if (!parent) {
			throw new Error(`转换实体重建批次缺少并查集节点: ${semanticKey}`);
		}
		if (parent === semanticKey) {
			return semanticKey;
		}
		const root = this.findRoot(parentBySemanticKey, parent);
		parentBySemanticKey.set(semanticKey, root);
		return root;
	}

	/**
	 * 合并两个语义组，并使用字典序较小的根保证 batchKey 稳定。
	 *
	 * @param parentBySemanticKey 并查集父节点表。
	 * @param leftSemanticKey 左侧语义 key。
	 * @param rightSemanticKey 右侧语义 key。
	 */
	private union(
		parentBySemanticKey: Map<string, string>,
		leftSemanticKey: string,
		rightSemanticKey: string,
	): void {
		const leftRoot = this.findRoot(parentBySemanticKey, leftSemanticKey);
		const rightRoot = this.findRoot(parentBySemanticKey, rightSemanticKey);
		if (leftRoot === rightRoot) {
			return;
		}
		const [keeperRoot, mergedRoot] = [leftRoot, rightRoot].sort();
		parentBySemanticKey.set(mergedRoot, keeperRoot);
	}
}
